import type { ComponentChildren, CSSProperties, HTMLAttributes } from 'preact';
import { createContext, toChildArray } from 'preact';
import { useContext } from 'preact/hooks';
import { useWide } from '../shell/hooks';
import { NeedDot } from './bits';

// The one table (consistency pass R6, Kristi Nov 2): every list whose rows share the same fields.
// T1 a white sheet, 1 px grey border, 10 px corners, on the page. T2 one shaded header in small
// capitals (12 px, 600), pinned under the bar while you scroll. T3 every column named but the
// actions column (its buttons name themselves). T4 only columns that sort carry ⇅; the sorted one
// is teal with ▲ or ▼; tapping a header sorts, and the Sort ▾ pill always agrees. T5 one row:
// 14 px, the book as title over author, numbers right-aligned in even-width figures. T6 a row that
// needs you has a teal left edge and the dot; the tint means selected only. T7 quiet rows (done,
// skipped, gone) turn their words grey. T8 on a phone there's no header: each row is a block, the
// book and its one control on the first line, the rest as one grey line. T9 the list foot sits
// under the sheet; an empty result is one line inside it.
// The sorting rule: every table sorts by its headers unless the order is itself the information
// (your own order; a record's time order). A table that doesn't sort shows no ⇅ and says its
// order in a quiet line above it (`order`: "In your list’s sequence", "Newest first").

export interface Col<K extends string = string> {
  /** The header's words; leave empty for the actions column (T3). */
  label?: ComponentChildren;
  /** Its grid track: '124px', 'minmax(0, 1fr)'. */
  width: string;
  /** Tapping the header sorts by this key (T4). */
  sort?: K;
  /** Numbers with units (★ 4.12, 7 mo, £8.99): right-aligned, even-width figures (T5). */
  num?: boolean;
  /** The book column: a row that needs you shows its dot here (T6). */
  book?: boolean;
  /** The header's tooltip ("Goodreads"). */
  title?: string;
  class?: string;
}

export interface TableSort<K extends string = string> {
  key: K;
  /** ▼ (descending) rather than ▲. */
  desc?: boolean;
}

interface Ctx {
  cols: Col[];
  narrow: boolean;
}
const TableCtx = createContext<Ctx>({ cols: [], narrow: false });

const track = (cols: Col[]) => cols.map((c) => c.width).join(' ');

/** A sortable column header: ⇅ while not sorted; teal with ▲ / ▼ while it is (T4). */
export function SortHead<K extends string>({
  label,
  k,
  sort,
  onSort,
  title,
  num,
  class: cls,
}: {
  label: ComponentChildren;
  k: K;
  sort: TableSort<K> | null | undefined;
  onSort: (k: K) => void;
  title?: string;
  num?: boolean;
  class?: string;
}) {
  const on = sort?.key === k;
  return (
    <span role="columnheader" class={`lt-h${num ? ' lt-num' : ''}${cls ? ` ${cls}` : ''}`} aria-sort={on ? (sort!.desc ? 'descending' : 'ascending') : 'none'}>
      <button type="button" class={`sort-head${on ? ' on' : ''}`} title={title} onClick={() => onSort(k)}>
        {label}
        <span class="sort-mark" aria-hidden="true">
          {on ? (sort!.desc ? ' ▼' : ' ▲') : ' ⇅'}
        </span>
      </button>
    </span>
  );
}

export function ListTable<K extends string = string>({
  label,
  cols,
  sort,
  onSort,
  order,
  empty = 'Nothing here matches.',
  onClear,
  foot,
  narrow: narrowProp,
  stickyTop,
  class: cls = '',
  children,
}: {
  /** The table's name for screen readers ("Up Next"). */
  label: string;
  cols: Col<K>[];
  /** The sort in force; the sorted column's header is teal. */
  sort?: TableSort<K> | null;
  /** Tapping a sortable header (the screen flips the direction on a second tap). */
  onSort?: (k: K) => void;
  /** For a table that doesn't sort: its order in words, shown above it (the sorting rule). */
  order?: string;
  /** The line inside the sheet when there are no rows (T9). */
  empty?: ComponentChildren;
  /** Adds "Clear filters" to the empty line. */
  onClear?: () => void;
  /** Under the sheet: the list foot (ListFoot). */
  foot?: ComponentChildren;
  /** The phone layout (T8). Defaults to the app's phone layout (under 700 px). */
  narrow?: boolean;
  /** Where the header pins: under the app's top bar and the page's pinned bar. */
  stickyTop?: string;
  class?: string;
  /** The rows: ListRow. */
  children?: ComponentChildren;
}) {
  const wide = useWide();
  const narrow = narrowProp ?? !wide;
  const rows = toChildArray(children).filter(Boolean);
  const sorts = !!onSort && cols.some((c) => c.sort);
  const style = { '--lt-cols': track(cols), ...(stickyTop ? { '--lt-top': stickyTop } : {}) } as CSSProperties;
  return (
    <TableCtx.Provider value={{ cols: cols as Col[], narrow }}>
      {!sorts && order ? <p class="lt-order">{order}</p> : null}
      <div class={`list-table${narrow ? ' narrow' : ''}${cls ? ` ${cls}` : ''}`} role="table" aria-label={label} style={style}>
        {!narrow ? (
          <div class="lt-row lt-head" role="row">
            {cols.map((c, i) =>
              c.sort && onSort ? (
                <SortHead key={i} label={c.label} k={c.sort} sort={sort} onSort={onSort} title={c.title} num={c.num} class={c.class} />
              ) : (
                <span key={i} role="columnheader" class={`lt-h lt-h-plain${c.num ? ' lt-num' : ''}${c.class ? ` ${c.class}` : ''}`} title={c.title}>
                  {c.label}
                </span>
              ),
            )}
          </div>
        ) : null}
        {rows.length ? (
          rows
        ) : (
          <div class="lt-empty" role="row">
            <span role="cell">
              {empty}
              {onClear ? (
                <>
                  {' '}
                  <button type="button" class="btn-link" onClick={onClear}>
                    Clear filters
                  </button>
                </>
              ) : null}
            </span>
          </div>
        )}
      </div>
      {foot}
    </TableCtx.Provider>
  );
}

/**
 * One row. `cells` follow the table's columns. `phone` is what the row shows on a phone (T8):
 * the book, its one control, and the other columns as one grey line ("Absorbing · ● 4 · ★ 4.12").
 * Without `phone`, the cells simply wrap. Any other attribute (data-row, onPointerDown…) goes on
 * the row itself.
 */
export function ListRow({
  cells,
  phone,
  need,
  selected,
  quiet,
  class: cls = '',
  ...rest
}: {
  cells: ComponentChildren[];
  phone?: {
    /** Before the book on a phone: a checkbox, a drag handle, a rank. */
    lead?: ComponentChildren;
    book: ComponentChildren;
    control?: ComponentChildren;
    line?: ComponentChildren;
  };
  /** The row needs you: a teal left edge and the dot before the book, with this tip (T6). */
  need?: string;
  /** Chosen with Select several: the pale teal tint (T6). */
  selected?: boolean;
  /** Done, skipped, gone or passed on: grey words (T7). */
  quiet?: boolean;
  class?: string;
} & Omit<HTMLAttributes<HTMLDivElement>, 'class' | 'cells'>) {
  const { cols, narrow } = useContext(TableCtx);
  const classes = `lt-row${need ? ' need' : ''}${selected ? ' sel' : ''}${quiet ? ' quiet' : ''}${cls ? ` ${cls}` : ''}`;
  const dot = need ? <NeedDot tip={need} /> : null;
  if (narrow && phone) {
    return (
      <div {...rest} role="row" class={`${classes} lt-block`}>
        {dot}
        {phone.lead ? (
          <span role="cell" class="lt-lead">
            {phone.lead}
          </span>
        ) : null}
        <span role="cell" class="lt-main">
          {phone.book}
        </span>
        {phone.control ? (
          <span role="cell" class="lt-control">
            {phone.control}
          </span>
        ) : null}
        {phone.line ? (
          <span role="cell" class="lt-line">
            {phone.line}
          </span>
        ) : null}
      </div>
    );
  }
  return (
    <div {...rest} role="row" class={`${classes}${narrow ? ' lt-wrap' : ''}`}>
      {/* The dot sits right against the teal edge, whatever the first column is (check-up, Kristi Nov 2). */}
      {dot}
      {cells.map((cell, i) => {
        const c = cols[i];
        return (
          <span key={i} role="cell" class={`lt-c${c?.num ? ' lt-num' : ''}${c?.book ? ' lt-bookcol' : ''}${c?.class ? ` ${c.class}` : ''}`}>
            {cell}
          </span>
        );
      })}
    </div>
  );
}

/** The book cell (T5): the title in the serif, 600, over the author in small grey; cover optional. */
export function BookCell({ title, author, cover, onOpen }: { title: string; author?: ComponentChildren; cover?: string | null; onOpen?: () => void }) {
  return (
    <span class="lt-book">
      {cover !== undefined ? (
        <span class="lt-cover" aria-hidden="true">
          {cover ? <img src={cover} alt="" loading="lazy" /> : null}
        </span>
      ) : null}
      <span class="lt-titles">
        {onOpen ? (
          <button type="button" class="lt-title" onClick={onOpen}>
            {title}
          </button>
        ) : (
          <span class="lt-title">{title}</span>
        )}
        {author ? <span class="lt-author">{author}</span> : null}
      </span>
    </span>
  );
}
